<template>
  <div class="modal-mask">
    <div class="modal request-modal">
      <h3>Richiedi Accesso</h3>
      <p class="subtitle">
        Non hai i permessi per <strong>{{ permissionLabel }}</strong> su questo dispositivo.
        <br>
        Puoi inviare una richiesta all'amministratore.
      </p>

      <div class="message-box">
        <label>Messaggio (opzionale)</label>
        <textarea 
          v-model="message" 
          placeholder="Es. Ho bisogno di modificare lo stato per manutenzione..."
          rows="3"
        ></textarea>
      </div>

      <div class="modal-actions">
        <button 
            :disabled="loading" 
            class="send-btn"
            @click="sendRequest"
        >
          <span v-if="loading">Invio...</span>
          <span v-else>Invia richiesta</span>
        </button>
        <button 
            :disabled="loading" 
            class="cancel-btn"
            @click="$emit('close')"
        >
          Annulla
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import axios from '@/axiosInstance';
import { useToast } from 'vue-toast-notification';

const props = defineProps<{
  deviceId: number;
  deviceName?: string;
  permissionType: 'change_status' | 'change_mode' | 'change_thresholds';
}>();

const emit = defineEmits(['close']);
const toast = useToast();

const message = ref("");
const loading = ref(false);

const permissionLabel = computed(() => {
    switch (props.permissionType) {
        case 'change_status': return 'modificare lo stato';
        case 'change_mode': return 'cambiare modalità (statico/dinamico)';
        case 'change_thresholds': return 'modificare le soglie';
        default: return 'effettuare questa azione';
    }
});

async function sendRequest() {
  loading.value = true;
  try {
    await axios.post(`/devices/${props.deviceId}/request-permission`, {
        permissionType: props.permissionType,
        message: message.value
    });
    
    toast.success("Richiesta inviata all'amministratore", { position: 'top-right', duration: 3000 });
    emit('close');
  } catch (e: any) {
    console.error(e);
    
    // Handle cooldown error (409 Conflict)
    if (e.response?.status === 409) {
        // Extract message from various possible response structures
        let cooldownMsg = "Hai già inviato una richiesta recentemente. Riprova più tardi.";
        
        const responseData = e.response?.data;
        if (typeof responseData === 'string') {
            cooldownMsg = responseData;
        } else if (responseData?.details) {
            cooldownMsg = responseData.details;
        } else if (responseData?.message) {
            cooldownMsg = responseData.message;
        } else if (responseData?.error) {
            cooldownMsg = responseData.error;
        }
        
        toast.warning(cooldownMsg, { position: 'top-right', duration: 5000 });
        emit('close'); // Close modal after showing cooldown message
    } else {
        // Generic error
        const errorMsg = e.response?.data?.message || e.response?.data?.details || "Errore nell'invio della richiesta";
        toast.error(errorMsg, { position: 'top-right', duration: 4000 });
    }
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.request-modal {
  background: #ffffff; /* Light theme for Google Sheets vibe? Or stick to dark? User said "Google Sheets style UI" but app is Dark. Sticking to Dark/App Theme is safer usually. Let's use Dark App Theme but clean structure. */
  background: #0d0d0d;
  color: #fff;
  width: 400px;
  max-width: 90%;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 20px 50px rgba(0,0,0,0.5);
  border: 1px solid #333;
}

h3 {
  margin: 0 0 12px 0;
  font-size: 1.25rem;
}

.subtitle {
  color: #aaa;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 20px;
}

.message-box {
  margin-bottom: 24px;
}

.message-box label {
  display: block;
  font-size: 0.85rem;
  color: #ccc;
  margin-bottom: 6px;
}

textarea {
  width: 100%;
  background: #1a1a1a;
  border: 1px solid #444;
  color: #fff;
  padding: 10px;
  border-radius: 4px;
  font-family: inherit;
  resize: vertical;
}

textarea:focus {
  outline: none;
  border-color: #008f8f;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

button {
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9rem;
  border: none;
}

.send-btn {
  background: #008f8f;
  color: white;
}
.send-btn:hover:not(:disabled) {
  background: #00a7a7;
}
.send-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.cancel-btn {
  background: transparent;
  color: #ccc;
  border: 1px solid #444;
}
.cancel-btn:hover:not(:disabled) {
  border-color: #666;
  color: #fff;
}
</style>
